import { useId, useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import { ownerKindOf, type ResourceOwnerInput } from '@reflex/shared';

export interface OwnerSelectTeamOption {
  ownerTeamId: string;
  name: string;
}

interface OwnerSelectProps {
  /**
   * Current user's id. Required so the form can construct a valid
   * user-owned payload that the server will accept (the caller-can-own
   * check in the backend asserts the user id matches the request user).
   */
  currentUserId: string;
  /**
   * Active organization id. Only needed on the *selection* path: picking
   * "Organization" encodes to `{ ownerId: currentOrganizationId }`, so the
   * option is offered only when this is present. Display no longer depends
   * on it — {@link encodeOwner} derives the current option from the owner
   * id prefix (via `ownerKindOf`), matching the display path used by
   * `useResolvedOwner`.
   */
  currentOrganizationId?: string | null;
  /**
   * Teams the current user belongs to. Only these are offered when the
   * caller picks `team` ownership; the server enforces the same rule
   * via {@link assertCallerCanOwn}, so missing teams here are a UX
   * mistake, not a security one.
   */
  teams?: OwnerSelectTeamOption[];
  /**
   * Whether the caller is allowed to assign the resource to the
   * organization at large. Hosts default this to `true`; pages where
   * org-wide creation is restricted to super-admins should pass
   * `false`.
   */
  allowOrganization?: boolean;
  value: ResourceOwnerInput;
  onChange: (next: ResourceOwnerInput) => void;
  label?: string;
  description?: string;
  className?: string;
  disabled?: boolean;
}

const MINE_VALUE = 'mine';
const ORG_VALUE = 'organization';
const TEAM_PREFIX = 'team:';

/**
 * Owner picker for create/edit forms. Defaults to "Mine" — the user is
 * the safest implicit owner of a freshly-minted resource. Switching to
 * a team or organization is opt-in.
 */
export function OwnerSelect({
  currentUserId,
  currentOrganizationId,
  teams = [],
  allowOrganization = true,
  value,
  onChange,
  label = 'Owner',
  description,
  className,
  disabled,
}: OwnerSelectProps) {
  const labelId = useId();
  const stringValue = useMemo(
    () =>
      encodeOwner(value, {
        currentUserId,
        currentOrganizationId,
        teamIds: teams.map((t) => t.ownerTeamId),
      }),
    [value, currentUserId, currentOrganizationId, teams],
  );

  return (
    <div className={cn('space-y-1', className)}>
      <Label htmlFor={labelId}>{label}</Label>
      <Select
        value={stringValue}
        onValueChange={(next) =>
          onChange(decodeOwner(next, { currentUserId, currentOrganizationId }))
        }
        disabled={disabled}
      >
        <SelectTrigger id={labelId} className="w-full" aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={MINE_VALUE}>Mine</SelectItem>
          {allowOrganization && currentOrganizationId ? (
            <SelectItem value={ORG_VALUE}>Organization</SelectItem>
          ) : null}
          {teams.length > 0 ? (
            <>
              <SelectSeparator />
              <SelectGroup>
                <SelectLabel>Teams</SelectLabel>
                {teams.map((t) => (
                  <SelectItem key={t.ownerTeamId} value={`${TEAM_PREFIX}${t.ownerTeamId}`}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            </>
          ) : null}
        </SelectContent>
      </Select>
      {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
    </div>
  );
}

/**
 * Whether an owner id denotes "the organization".
 *
 * Identity first, prefix second. `ownerKindOf` derives the kind from the
 * `org_` id prefix, which every minted organization carries — but the
 * seeded single-tenant organization is the bare literal `SYSTEM_ORG_ID`
 * (`'system'`), with no prefix at all. Prefix alone therefore reports an
 * org-owned row on a single-tenant install as unrecognised, and the picker
 * falls through to displaying "Mine" over a value that is not the caller.
 * Comparing against the active org id first covers that install and every
 * future id shape; the prefix check stays as the fallback for callers with
 * no org context to compare against.
 *
 * Exported because {@link OwnerField} must ask the same question to decide
 * whether to keep the Organization option available for the current value.
 */
export function isOrganizationOwned(
  ownerId: string | null | undefined,
  currentOrganizationId?: string | null,
): boolean {
  if (ownerId == null) return false;
  if (currentOrganizationId != null && ownerId === currentOrganizationId) return true;
  return ownerKindOf(ownerId) === 'organization';
}

function encodeOwner(
  value: ResourceOwnerInput,
  ctx: { currentUserId: string; currentOrganizationId?: string | null; teamIds: string[] },
): string {
  // Derive the option from the owner id, matching the rest of the ownership
  // UI (`useResolvedOwner` / `OwnerChip`).
  if (isOrganizationOwned(value.ownerId, ctx.currentOrganizationId)) return ORG_VALUE;
  const kind = ownerKindOf(value.ownerId);
  if (kind === 'team' && value.ownerId && ctx.teamIds.includes(value.ownerId)) {
    return `${TEAM_PREFIX}${value.ownerId}`;
  }
  // Fallback: the current user, any unrecognised owner id (e.g. a team the
  // user isn't on, a plugin-owned id, or a stale value), and null all
  // decode as "Mine" so the picker never gets stuck on an unselectable
  // option.
  return MINE_VALUE;
}

function decodeOwner(
  raw: string,
  ctx: { currentUserId: string; currentOrganizationId?: string | null },
): ResourceOwnerInput {
  if (raw === ORG_VALUE) {
    return { ownerId: ctx.currentOrganizationId ?? null };
  }
  if (raw.startsWith(TEAM_PREFIX)) {
    const teamId = raw.slice(TEAM_PREFIX.length);
    if (teamId) {
      return { ownerId: teamId };
    }
  }
  return { ownerId: ctx.currentUserId };
}
